import { useEffect, useState } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { formatDateTimeAU } from "@/lib/formatDateAU";

const CAPS = [
  {
    key: "can_refer",
    label: "Referral Partner",
    desc: "Allows tracked AWARE referral links/QR and referral attribution subject to the Partner's commission agreement.",
  },
  {
    key: "can_purchase_kits",
    label: "Purchase AWARE Kits",
    desc: "Allows the Partner to purchase AWARE kits upfront using the approved Partner kit process. Independent of Retail Stockist status.",
  },
  {
    key: "can_retail",
    label: "Retail Stockist",
    desc: "Allows the Partner to create Retail Counter bookings and sell AWARE testing through its business. Requires explicit approval.",
  },
  {
    key: "invoice_account_enabled",
    label: "Invoice Account",
    desc: "Allows eligible Partner charges to be placed on approved Partner invoice terms.",
  },
];

export default function PartnerCapabilitiesSection({ partner }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(null);

  const load = () =>
    api.get(`/admin/partners/${partner.id}/capabilities`)
      .then(({ data }) => { setData(data); setError(false); })
      .catch(() => setError(true));
  useEffect(() => { load(); }, [partner.id]);

  const toggle = async (key, value) => {
    setSaving(key);
    try {
      await api.put(`/admin/partners/${partner.id}/capabilities`, { [key]: value });
      toast.success(`${CAPS.find((c) => c.key === key)?.label} ${value ? "enabled" : "disabled"}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update capability");
    } finally {
      setSaving(null);
    }
  };

  if (error) {
    return (
      <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800" data-testid="partner-capabilities-error">
        Could not load Partner Capabilities. <button onClick={load} className="font-semibold underline">Retry</button>
      </div>
    );
  }
  if (!data) return null;
  const caps = data.capabilities || {};

  return (
    <div className="mt-5 rounded-2xl border border-slate-200 p-4" data-testid="partner-capabilities-section">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-cyan-600" />
        <h3 className="font-display font-semibold text-slate-900">Partner Capabilities</h3>
      </div>
      <p className="mt-1 text-[11px] text-slate-500">
        Admin-only controls. Every change is audited. Capabilities are additive — a Partner may
        hold any combination.
      </p>
      <div className="mt-3 space-y-2.5">
        {CAPS.map(({ key, label, desc }) => (
          <div key={key} className="flex items-start justify-between gap-4 rounded-xl bg-slate-50 p-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800">{label}</div>
              <div className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{desc}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0 pt-0.5">
              {saving === key && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
              <Switch
                data-testid={`partner-cap-toggle-${key}`}
                checked={!!caps[key]}
                disabled={saving !== null}
                onCheckedChange={(v) => toggle(key, v)}
              />
            </div>
          </div>
        ))}
      </div>
      {(data.updated_at || (data.audit || []).length > 0) && (
        <div className="mt-2.5 text-[11px] text-slate-400" data-testid="partner-cap-audit-note">
          {data.updated_at
            ? <>Last changed {formatDateTimeAU(data.updated_at)}{data.updated_by ? ` by ${data.updated_by}` : ""} · </>
            : null}
          {(data.audit || []).length} audit record{(data.audit || []).length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}
